import React, { useCallback, useMemo } from "react";

import { faInfoCircle } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useIntl } from "react-intl";

import { Company, EntityType, MapEntity } from "@mapmycustomers/shared/types/entity";

import ButtonLink from "@app/component/ButtonLink";
import { showEntityView } from "@app/store/entityView/actions";
import { formatCoordinates } from "@app/util/formatters";
import getShortAddress from "@app/util/ui/getShortAddress";

import styles from "./EntityItem.module.scss";
import messages from "./messages";
import AnnotationData from "./types/AnnotationData";

interface Props {
  entity: MapEntity;
  fullWidth?: boolean;
  iconGetter: (entity: MapEntity) => string | undefined;
  onShowAnnotation?: (annotation?: AnnotationData) => void;
  onShowPreviewPane: typeof showEntityView;
}

const EntityItem: React.FC<Props> = ({
  entity,
  fullWidth,
  iconGetter,
  onShowAnnotation,
  onShowPreviewPane,
}) => {
  const intl = useIntl();

  const icon = useMemo(() => iconGetter(entity), [entity, iconGetter]);

  // we need parentEntityType, because parentEntity we receive from /pins doesn't have "entity" field
  const [parentEntity, parentEntityType] = useMemo(() => {
    switch (entity.entity) {
      case EntityType.COMPANY:
        return [entity.parentAccount, EntityType.COMPANY];
      case EntityType.PERSON:
        return [
          (entity.primaryAccount ?? entity.accounts?.[0]) as Company | undefined,
          EntityType.COMPANY,
        ];
      case EntityType.DEAL:
        return [
          entity.account ?? entity.contact,
          entity.account ? EntityType.COMPANY : EntityType.PERSON,
        ];
    }
  }, [entity]);

  const handleParentEntityClick = useCallback(() => {
    if (!parentEntity) {
      return;
    }
    onShowPreviewPane?.({ entityId: parentEntity.id, entityType: parentEntityType });
  }, [onShowPreviewPane, parentEntity, parentEntityType]);

  const handleMouseEnter = useCallback(
    (e) => {
      onShowAnnotation?.({
        coordinates: { left: e.clientX, top: e.clientY },
        entityId: entity.id,
        entityType: entity.entity,
      });
    },
    [entity, onShowAnnotation]
  );

  const handleClick = useCallback(() => {
    onShowPreviewPane({ entityId: entity.id, entityType: entity.entity });
  }, [entity, onShowPreviewPane]);

  const details = (
    <div className={styles.details}>
      {entity.entity === EntityType.DEAL
        ? entity.stage.name
        : getShortAddress(entity.geoAddress) || formatCoordinates(entity.geoPoint?.coordinates)}
      {parentEntity ? (
        <>
          &nbsp;•&nbsp;
          <ButtonLink onClick={handleParentEntityClick}>{parentEntity.name}</ButtonLink>
        </>
      ) : null}
    </div>
  );

  return (
    <div className={styles.container} onClick={handleClick}>
      <div className={styles.iconContainer}>
        <img alt="record icon" src={icon} />
      </div>
      <div className={styles.data}>
        <div className={styles.titleContainer}>
          <span className={styles.title}>{entity.name}</span>
          <FontAwesomeIcon
            className={styles.infoIcon}
            icon={faInfoCircle}
            onMouseEnter={handleMouseEnter}
          />
        </div>
        {fullWidth && details}
      </div>
      <div className={styles.cadence}>
        {intl.formatMessage(messages.pastDue, {
          days: entity.cadenceDaysOut,
          neverContacted: entity.cadenceDaysOut == null,
        })}
      </div>
      {!fullWidth && details}
    </div>
  );
};

export default EntityItem;
